import type { ApiCategoriesUrlOrder } from '../../models/recipe/recipeModel';
import { Browser, BrowserSize } from '../../utility/browser';
import type { RecipeCategoryFacet } from '../search/models/models';
import type {
    CategoryNamesUnderLevel1,
    CategoryNameToUrlSegment,
    GetUrlForCategoriesSignature,
    RecipeCategoryFallbackImages,
} from './models';
/**
 *
 * @param currStringArray current state of array
 * @param newString to be added or removed from currStringArray
 * @returns new array with or without newString
 */
export const createNewArrayByTogglingString = (currStringArray: string[], newString: string) => {
    const shouldRemoveString = currStringArray.includes(newString);

    if (shouldRemoveString) {
        return currStringArray.filter((name) => name !== newString);
    }

    return [...currStringArray, newString];
};

/**
 * @returns a function that creates a path string where the categories are ordered correctly
 */
export const createGetUrlForCategories =
    (
        categoryNameToUrlSegment: CategoryNameToUrlSegment,
        categoriesUrlOrder: ApiCategoriesUrlOrder,
        recipeRootUrl: string,
    ): GetUrlForCategoriesSignature =>
    ({ categories }: { categories: string[] }) => {
        const sortByCategoryUrlOrder = (a: string, b: string) =>
            categoriesUrlOrder[a.toLowerCase()] - categoriesUrlOrder[b.toLowerCase()];

        const mapCategoryNameToUrlSegment = (categoryName: string) =>
            categoryName ? categoryNameToUrlSegment[categoryName.toLowerCase()] : null;

        const removedPotentialLastSlashFromRoot =
            recipeRootUrl.slice(-1) === '/'
                ? recipeRootUrl.slice(0, recipeRootUrl.length - 1)
                : recipeRootUrl;

        const hasFilterQuery = categories.length > 2;

        const builtPath = categories
            .slice()
            .sort(sortByCategoryUrlOrder)
            .map(mapCategoryNameToUrlSegment)
            .filter(Boolean)
            .reduce((acc, currSegment, index) => {
                if (index < 2) {
                    return `${acc}/${currSegment}`;
                }

                if (index === 2) {
                    return `${acc}?filter=${currSegment}`;
                }

                return `${acc},${currSegment}`;
            }, removedPotentialLastSlashFromRoot);

        return hasFilterQuery ? builtPath : `${builtPath}/`;
    };

export const getCategoriesWhileRemovingCategoryFacetChildren = ({
    currentCategories,
    category,
}: {
    currentCategories: string[];
    category: RecipeCategoryFacet;
}) => {
    if (Array.isArray(category.categories)) {
        const childrenNames = category.categories.map((childCategory) =>
            childCategory.item.toLowerCase(),
        );

        const categoriesWithoutChildren = currentCategories.filter(
            (currCategory) => !childrenNames.includes(currCategory),
        );

        return createNewArrayByTogglingString(
            categoriesWithoutChildren,
            category.item.toLowerCase(),
        );
    }

    return createNewArrayByTogglingString(currentCategories, category.item.toLowerCase());
};

export const getCategoriesWhileRemovingCategoryFacetsParent = ({
    currentCategories,
    category,
    parentCategory,
}: {
    currentCategories: string[];
    category: RecipeCategoryFacet;
    parentCategory: RecipeCategoryFacet;
}) => {
    const lowerCaseParentName = parentCategory.item.toLowerCase();

    const categoriesWithoutParent = currentCategories.filter(
        (categoryName) => categoryName !== lowerCaseParentName,
    );

    return createNewArrayByTogglingString(categoriesWithoutParent, category.item.toLowerCase());
};

export const getAllCategoryNamesFromLevel2AndDown = (
    categories: RecipeCategoryFacet[] | null,
    collectedNames: string[] = [],
    rootCall: boolean = true,
): string[] => {
    if (!categories) {
        return collectedNames;
    }

    return categories.reduce((acc, facet) => {
        const childCategories = getAllCategoryNamesFromLevel2AndDown(
            facet.categories,
            collectedNames,
            false,
        );

        if (rootCall) {
            return [...acc, ...childCategories];
        }

        return [...acc, ...childCategories, facet.item];
    }, collectedNames);
};

/**
 * Removes categories on level 1
 * @returns the categories we can show
 */
export const getCategoryNamesUnderLevel1 = (
    ssrCategoriesUrlOrder: ApiCategoriesUrlOrder,
): CategoryNamesUnderLevel1 => {
    const obj = ssrCategoriesUrlOrder ?? {};
    return Object.entries(obj).reduce((acc, [key, val]) => {
        if (val % 1000 !== 0) {
            return {
                ...acc,
                [key]: true,
            };
        }

        return acc;
    }, {});
};

export const buildRecipeRedirectUrl = (externalId: string) => `/redirect/recipe/${externalId}`;

export const getHeroSrcSetSizes = (
    width: number,
    height: number,
    customRatio?: number,
    breakpoints = {
        xxsm: Browser.getBreakpoint(BrowserSize.xxsm).from,
        xsm: Browser.getBreakpoint(BrowserSize.xsm).from,
        xlg: Browser.getBreakpoint(BrowserSize.xlg).from,
    },
) => {
    const ratio = customRatio ?? height / width;

    return Object.fromEntries(
        Object.entries(breakpoints).map(([key, w]) => [
            key,
            { width: w, height: Math.round(w * ratio) },
        ]),
    );
};

export const getCategoryFallbackImages = (
    wideDesktopImage?: string,
    mobileImage?: string,
    defaultWideImage?: string,
    defaultMobileImage?: string,
) => {
    let fullWidth: string | undefined;
    let mobile: string | undefined;

    if (wideDesktopImage || mobileImage) {
        fullWidth = wideDesktopImage;
        mobile = mobileImage;
    } else {
        const backupImage =
            'https://res.cloudinary.com/coopsverige/images/c_fill,g_center,w_1200,h_630,f_auto,fl_progressive,q_90/v1623139556/227862/Glasnudelsallad%20med%20r%C3%A4kor.jpg';

        fullWidth = defaultWideImage ?? backupImage;
        mobile = defaultMobileImage ?? backupImage;
    }

    const returnMe: RecipeCategoryFallbackImages = {
        fullWidth,
        mobile,
    };

    return returnMe;
};
